import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useConfirm } from "../components/ConfirmDialog";

export default function Messages() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const confirm = useConfirm();

  const load = () => {
    setLoading(true);
    api.get("/messages/threads").then(({ data }) => setThreads(data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const deleteThread = async (otherId, e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await confirm({
      title: "¿Eliminar esta conversación?",
      message: "Se borrarán todos los mensajes de este chat de forma permanente.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await api.delete(`/messages/thread/${otherId}`);
      setThreads((arr) => arr.filter((t) => t.other_user?.id !== otherId));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="space-y-5 animate-fade-in-up max-w-2xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-extrabold">Mensajes</h1>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>}

      {loading ? (
        <div className="text-center py-16 text-slate-500">Cargando…</div>
      ) : threads.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="messages-empty">
          <i className="fa-regular fa-comment text-5xl text-slate-300"></i>
          <p className="mt-4 font-bold text-slate-700">Sin mensajes</p>
          <p className="text-sm">Cuando contactes a un canguro o profesor, las conversaciones aparecerán aquí</p>
          <Link to="/home" className="btn-primary mt-6 inline-flex" data-testid="messages-explore-btn">Explorar perfiles</Link>
        </div>
      ) : (
        <div className="kibli-card overflow-hidden divide-y divide-slate-100">
          {threads.map((t) => {
            const ou = t.other_user || {};
            const initials = (ou.name || "?").substring(0, 1).toUpperCase();
            const unread = t.unread_count || 0;
            return (
              <div key={t.thread_key} className="group flex items-center gap-3 p-4 hover:bg-slate-50 transition" data-testid={`thread-${ou.id}`}>
                <Link to={`/messages/with/${ou.id}`} className="flex-1 min-w-0 flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">{initials}</div>
                    {unread > 0 && (
                      <span
                        data-testid={`thread-unread-dot-${ou.id}`}
                        className="absolute -top-0.5 -right-0.5 min-w-[20px] h-[20px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shadow ring-2 ring-white"
                      >
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`truncate ${unread > 0 ? "font-extrabold text-slate-900" : "font-bold"}`}>{ou.name || "Desconocido"}</div>
                    <div className={`text-sm truncate ${unread > 0 ? "text-slate-800 font-semibold" : "text-slate-500"}`}>{t.last_message}</div>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={(e) => deleteThread(ou.id, e)}
                  data-testid={`delete-thread-${ou.id}`}
                  className="opacity-60 md:opacity-0 md:group-hover:opacity-100 transition-opacity w-9 h-9 flex items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600"
                  title="Eliminar conversación"
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
